import { useMemo } from 'react'
import { Box, Drawer, IconButton, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { MentorClinicsContext } from 'src/mentor/layout'
import ClinicReportsWithVisualizations from 'src/mentor/components/MentorClinicBookingsPage/component/ClinicReportsWithVisualizations'
import { DEFAULT_STATS_API, StatsApiContext } from 'src/shared/stats/statsApiContext'

// Same payload shapes as the clinic-owner endpoints, served read-only for any brand to
// ROLE_CLINIC_STATS_ADMIN (see backend ClinicStatsAdminController).
const SUPPORT_CLINIC_STATS_API = Object.freeze({
  ...DEFAULT_STATS_API,
  clinicReportsPath: '/api/secure/cc/support/clinic-stats/get-reports',
  patientSegmentationPath: '/api/secure/cc/support/clinic-stats/patient-segmentation',
})

/**
 * Support-portal "Clinic Stats" drawer — renders the EXACT owner stats framework
 * (ClinicReportsWithVisualizations) for one clinic brand, locked to that brand, against the
 * read-only clinic-stats admin endpoints. The component reads its brand/location inputs from
 * MentorClinicsContext, so we provide a context scoped to the picked brand; any enhancement to the
 * shared visualizations shows up here automatically.
 *
 * @param brand      brand summary from the clinics listing ({ id, title, slug, ... })
 * @param locations  the brand's clinic location profiles (ClinicLocProfile beans)
 */
export default function SupportClinicStatsDrawer({ open, onClose, brand, locations }) {
  const clinicsContextValue = useMemo(
    () => ({
      associatedClinicBrands: brand
        ? [{ ...brand, enabled: true, brandName: brand?.title || brand?.brandName }]
        : [],
      associatedClinicsData: locations || [],
      loadingAssociationsData: false,
    }),
    [brand, locations],
  )

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', md: '92%', lg: '85%' }, maxWidth: '1500px' } }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          position: 'sticky',
          top: 0,
          zIndex: 5,
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="overline" sx={{ color: '#418571', fontWeight: 700, letterSpacing: 1 }}>
            Clinic Stats
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }} noWrap>
            {brand?.title || brand?.brandName || 'Clinic Brand'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} aria-label="Close clinic stats">
          <CloseIcon />
        </IconButton>
      </Box>
      <Box sx={{ overflowY: 'auto', flex: 1 }}>
        {open && brand?.id ? (
          <StatsApiContext.Provider value={SUPPORT_CLINIC_STATS_API}>
            <MentorClinicsContext.Provider value={clinicsContextValue}>
              <ClinicReportsWithVisualizations lockedClinicBrandId={brand.id} />
            </MentorClinicsContext.Provider>
          </StatsApiContext.Provider>
        ) : null}
      </Box>
    </Drawer>
  )
}
